import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Box, Divider, Chip, Grid
} from '@mui/material';
import ApiService from '../Services/apiService.ts';

type Consent = {
  UserId: string;
  formType?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth?: string;
  age?: string;
  gender?: string;
  address?: string;
  physician?: string;
  allergies?: string;
  bleedingDisorders?: string;
  medicalConditions?: string;
  medications?: string;
  emergencyContact?: string;
  howDidYouHear?: string;
  typeOfPiercing?: string;
  typeOfJewelryUsed?: string;
  nameOfPiercer?: string;
  acknowledgments?: { [key: string]: { checked: boolean; label: string } };
  initials?: { [key: string]: { checked: boolean; label: string } };
  clientSignature?: string;
  piercerSignature?: string;
  artistSignature?: string;
  signature?: string;
  date?: string;
  piercerDate?: string;
  photoIdVerified?: string;
  photoIdUrl?: string;
  photoIdS3Key?: string;
  notarizedDocUrl?: string;
  notarizedDocS3Key?: string;
  guardianSignatureUrl?: string;
  piercerSignatureUrl?: string;
  guardianSignatureS3Key?: string;
  piercerSignatureS3Key?: string;
  placement?: string;
  designLocation?: string;
  tattooArtist?: string;
  idNumber?: string;
  createdAt: string;
  // Minor fields
  minorFirstName?: string;
  minorLastName?: string;
  relationship?: string;
  notaryName?: string;
  notarySignature?: string;
};

const CustomerConsents = () => {
  const [consents, setConsents] = useState<Consent[]>([]);
  const [filteredConsents, setFilteredConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConsent, setSelectedConsent] = useState<Consent | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);

  useEffect(() => {
    fetchConsents();
  }, []);

  const fetchConsents = async (cursor?: string) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const apiService = new ApiService();
      const response = await apiService.getUserConsents(50, cursor || undefined);

      const items = response.items || [];
      const newNextKey = response.nextKey || null;

      if (cursor) {
        // Append to existing results
        const combined = [...consents, ...items];
        setConsents(combined);
        setFilteredConsents(combined);
      } else {
        // Fresh load
        setConsents(items);
        setFilteredConsents(items);
      }

      setNextKey(newNextKey);
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      console.error('Error fetching consents:', error);
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (nextKey) {
      fetchConsents(nextKey);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    const filtered = consents.filter(consent =>
      consent.firstName.toLowerCase().includes(query) ||
      consent.lastName.toLowerCase().includes(query) ||
      consent.phone.includes(query)
    );

    setFilteredConsents(filtered);
  };

  const handleRowClick = (consent: Consent) => {
    setSelectedConsent(consent);
  };

  const handleCloseModal = () => {
    setSelectedConsent(null);
  };

  const DetailRow = ({ label, value }: { label: string; value?: string }) => {
    if (!value) return null;
    return (
      <Typography sx={{ mb: 0.5 }}>
        <b>{label}:</b> {value}
      </Typography>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
        Customer Consents
      </Typography>

      <TextField
        label="Search by Name or Phone"
        variant="outlined"
        fullWidth
        value={searchQuery}
        onChange={handleSearch}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#1976d2' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Phone</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Piercing</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date Submitted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConsents.map((consent, index) => (
                <TableRow
                  key={consent.UserId}
                  onClick={() => handleRowClick(consent)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                    '&:hover': { backgroundColor: '#e3f2fd' },
                  }}
                >
                  <TableCell>
                    {consent.formType === 'minor'
                      ? `${consent.minorFirstName || ''} ${consent.minorLastName || ''}`
                      : `${consent.firstName} ${consent.lastName}`
                    }
                  </TableCell>
                  <TableCell>{`+1 ${consent.phone}`}</TableCell>
                  <TableCell>
                    <Chip
                      label={consent.formType === 'minor' ? 'Minor' : 'Adult'}
                      color={consent.formType === 'minor' ? 'warning' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{consent.typeOfPiercing || consent.placement || '-'}</TableCell>
                  <TableCell>{new Date(consent.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Load More */}
      {!loading && nextKey && !searchQuery && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {loadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}

      {!loading && (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          Showing {filteredConsents.length} record{filteredConsents.length !== 1 ? 's' : ''}
        </Typography>
      )}

      {/* Consent Details Modal */}
      {selectedConsent && (
        <Dialog open={Boolean(selectedConsent)} onClose={handleCloseModal} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>
            Consent Details
            <Chip
              label={selectedConsent.formType === 'minor' ? 'Minor' : 'Adult'}
              color={selectedConsent.formType === 'minor' ? 'warning' : 'primary'}
              size="small"
              sx={{ ml: 2 }}
            />
          </DialogTitle>
          <DialogContent dividers>
            {/* Personal Information */}
            <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Personal Information</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <DetailRow label="First Name" value={selectedConsent.firstName} />
              </Grid>
              <Grid item xs={6}>
                <DetailRow label="Last Name" value={selectedConsent.lastName} />
              </Grid>
              <Grid item xs={6}>
                <DetailRow label="Phone" value={selectedConsent.phone ? `+1 ${selectedConsent.phone}` : ''} />
              </Grid>
              <Grid item xs={6}>
                <DetailRow label="Email" value={selectedConsent.email} />
              </Grid>
              <Grid item xs={4}>
                <DetailRow label="Date of Birth" value={selectedConsent.dateOfBirth} />
              </Grid>
              <Grid item xs={4}>
                <DetailRow label="Age" value={selectedConsent.age} />
              </Grid>
              <Grid item xs={4}>
                <DetailRow label="Gender" value={selectedConsent.gender} />
              </Grid>
              <Grid item xs={12}>
                <DetailRow label="Address" value={selectedConsent.address} />
              </Grid>
              <Grid item xs={12}>
                <DetailRow label="Physician" value={selectedConsent.physician} />
              </Grid>
            </Grid>

            {/* Minor Info (if applicable) */}
            {selectedConsent.formType === 'minor' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Minor Information</Typography>
                <DetailRow label="Minor's Name" value={`${selectedConsent.minorFirstName} ${selectedConsent.minorLastName}`} />
                <DetailRow label="Relationship" value={selectedConsent.relationship} />
              </>
            )}

            {/* Medical History */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Medical History</Typography>
            <DetailRow label="Allergies" value={selectedConsent.allergies || 'None'} />
            <DetailRow label="Bleeding Disorders" value={selectedConsent.bleedingDisorders || 'None'} />
            <DetailRow label="Medical Conditions" value={selectedConsent.medicalConditions || 'None'} />
            <DetailRow label="Medications" value={selectedConsent.medications || 'None'} />

            {/* Emergency & Referral */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Emergency & Referral</Typography>
            <DetailRow label="Emergency Contact" value={selectedConsent.emergencyContact} />
            <DetailRow label="How did you hear about us" value={selectedConsent.howDidYouHear} />

            {/* Procedure Information */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Procedure Information</Typography>
            <DetailRow label="Type of Piercing" value={selectedConsent.typeOfPiercing || selectedConsent.placement} />
            <DetailRow label="Type of Jewelry Used" value={selectedConsent.typeOfJewelryUsed} />
            <DetailRow label="Name of Piercer" value={selectedConsent.nameOfPiercer} />

            {/* Acknowledgments */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Acknowledged Statements</Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {Object.values(selectedConsent.acknowledgments || selectedConsent.initials || {}).map((item, index) => (
                <li key={index} style={{ marginBottom: '4px' }}>
                  <Typography variant="body2">{item.label}</Typography>
                </li>
              ))}
            </ul>

            {/* Signatures */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Signatures</Typography>

            {/* Client Signature */}
            {(selectedConsent.clientSignature && selectedConsent.clientSignature.startsWith('data:image')) || selectedConsent.guardianSignatureUrl ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {selectedConsent.formType === 'minor' ? 'Guardian Signature:' : 'Client Signature:'}
                </Typography>
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1, display: 'inline-block', mt: 0.5 }}>
                  <img
                    src={selectedConsent.guardianSignatureUrl || selectedConsent.clientSignature || selectedConsent.signature}
                    alt="Client Signature"
                    style={{ maxWidth: '100%', height: '100px' }}
                  />
                </Box>
                <DetailRow label="Date" value={selectedConsent.date} />
              </Box>
            ) : (
              <DetailRow label="Client Signature" value={selectedConsent.clientSignature || selectedConsent.signature} />
            )}

            {/* Piercer/Artist Signature */}
            {((selectedConsent.piercerSignature || selectedConsent.artistSignature) && 
             (selectedConsent.piercerSignature || selectedConsent.artistSignature)?.startsWith('data:image')) || selectedConsent.piercerSignatureUrl ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {selectedConsent.formType === 'tattooConsent' ? 'Artist Signature:' : 'Piercer Signature:'}
                </Typography>
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1, display: 'inline-block', mt: 0.5 }}>
                  <img
                    src={selectedConsent.piercerSignatureUrl || selectedConsent.piercerSignature || selectedConsent.artistSignature}
                    alt="Piercer/Artist Signature"
                    style={{ maxWidth: '100%', height: '100px' }}
                  />
                </Box>
                <DetailRow label="Date" value={selectedConsent.piercerDate} />
              </Box>
            ) : (
              (selectedConsent.piercerSignature || selectedConsent.artistSignature) && 
              <DetailRow label={selectedConsent.formType === 'tattooConsent' ? 'Artist Signature' : 'Piercer Signature'} 
                value={selectedConsent.piercerSignature || selectedConsent.artistSignature} />
            )}

            {/* Photo ID */}
            {(selectedConsent.photoIdUrl || selectedConsent.photoIdS3Key || selectedConsent.photoIdVerified) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>
                  {selectedConsent.formType === 'minor' ? "Guardian's Photo ID" : 'Photo ID'}
                </Typography>
                {selectedConsent.photoIdUrl ? (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        display: 'inline-block',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={selectedConsent.photoIdUrl}
                        alt="Customer Photo ID"
                        style={{ maxWidth: '100%', maxHeight: '300px', display: 'block' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Photo ID verified ✅
                    </Typography>
                  </Box>
                ) : selectedConsent.photoIdS3Key ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Photo ID uploaded ✅ (S3 key: {selectedConsent.photoIdS3Key})
                    </Typography>
                  </Box>
                ) : (
                  <DetailRow label="Photo ID Verified" value={selectedConsent.photoIdVerified === 'yes' ? '✅ Yes' : '❌ No'} />
                )}
              </>
            )}

            {/* Notarized Document (Minor forms only) */}
            {(selectedConsent.notarizedDocUrl || selectedConsent.notarizedDocS3Key) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: '#1976d2' }}>Notarized Consent Document</Typography>
                {selectedConsent.notarizedDocUrl ? (
                  <Box sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        display: 'inline-block',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={selectedConsent.notarizedDocUrl}
                        alt="Notarized Consent Document"
                        style={{ maxWidth: '100%', maxHeight: '300px', display: 'block' }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Notarized document uploaded ✅
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Notarized document uploaded ✅ (S3 key: {selectedConsent.notarizedDocS3Key})
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Metadata */}
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Submitted: {new Date(selectedConsent.createdAt).toLocaleString()}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal} variant="contained" color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
};

export default CustomerConsents;
